import React from 'react';
import { MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';

const CollegeSelector = ({
  colleges = [],
  selectedColleges = [],
  onSelectionChange,
  includeAdditional = false,
  onToggleAdditional
}) => {

  // Filter colleges based on includeAdditional setting
  const displayColleges = colleges.filter(college => {
    const category = college.category?.toLowerCase();
    if (category === 'additional' || category === 'other') {
      return includeAdditional;
    }
    return true;
  });

  // Group colleges by tier for quick select
  const collegesByTier = {
    reach: displayColleges.filter(c => c.category?.toLowerCase() === 'reach'),
    target: displayColleges.filter(c => c.category?.toLowerCase() === 'target'),
    safety: displayColleges.filter(c => c.category?.toLowerCase() === 'safety'),
    additional: displayColleges.filter(c =>
      c.category?.toLowerCase() === 'additional' ||
      c.category?.toLowerCase() === 'other'
    )
  };

  // Get selected college data for chips
  const selectedCollegeData = colleges.filter(c => selectedColleges.includes(c.id));

  const handleToggleCollege = (collegeId) => {
    if (selectedColleges.includes(collegeId)) {
      // Remove from selection
      onSelectionChange(selectedColleges.filter(id => id !== collegeId));
    } else if (selectedColleges.length >= 4) {
      // Show clear feedback when at limit
      toast.error(
        'Maximum 4 colleges can be compared at once. Remove a college to add another.',
        {
          duration: 4000,
          icon: '🎯',
          style: {
            background: '#FEF2F2',
            color: '#991B1B',
            border: '2px solid #DC2626',
          },
        }
      );
    } else {
      // Add to selection
      onSelectionChange([...selectedColleges, collegeId]);
    }
  };

  const handleQuickSelectTier = (tier) => {
    const tierColleges = collegesByTier[tier];
    if (tierColleges.length === 0) return;

    // Select up to 4 colleges from this tier
    const newSelection = tierColleges.slice(0, 4).map(c => c.id);
    onSelectionChange(newSelection);
    toast.success(`Selected ${tier} colleges for comparison`);
  };

  const getTierColors = (category) => {
    const normalizedCategory = category?.toLowerCase();
    switch (normalizedCategory) {
      case 'reach':
        return {
          chipBg: 'bg-rose-400',
          headerBg: 'bg-rose-400',
          selectedBorder: 'border-rose-800',
          unselectedBorder: 'border-rose-300',
          checkmarkColor: 'text-rose-600'
        };
      case 'target':
        return {
          chipBg: 'bg-amber-400',
          headerBg: 'bg-amber-400',
          selectedBorder: 'border-amber-800',
          unselectedBorder: 'border-amber-300',
          checkmarkColor: 'text-amber-600'
        };
      case 'safety':
        return {
          chipBg: 'bg-teal-400',
          headerBg: 'bg-teal-400',
          selectedBorder: 'border-teal-800',
          unselectedBorder: 'border-teal-300',
          checkmarkColor: 'text-teal-600'
        };
      default:
        return {
          chipBg: 'bg-slate-400',
          headerBg: 'bg-slate-400',
          selectedBorder: 'border-slate-800',
          unselectedBorder: 'border-slate-300',
          checkmarkColor: 'text-slate-600'
        };
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 border-slate-900 shadow-sm mb-6">
      {/* Header Section */}
      <div className="flex items-center justify-between px-8 py-6">
        <div>
          <h2 className="text-md font-bold text-slate-900">COMPARING {selectedCollegeData.length} OF 4</h2>
          <p className="text-sm text-slate-600 mt-1">Select schools for Comparative Analysis</p>
        </div>
        <div className="flex gap-3">
          {['reach', 'target', 'safety'].map(tier => (
            <button
              key={tier}
              onClick={() => handleQuickSelectTier(tier)}
              disabled={collegesByTier[tier].length === 0}
              className="px-4 py-2 bg-white border-2 border-slate-900 rounded-lg text-xs font-medium hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Quick {tier}
            </button>
          ))}
          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 rounded-lg text-sm cursor-pointer hover:bg-stone-50">
            <input
              type="checkbox"
              checked={includeAdditional}
              onChange={(e) => onToggleAdditional(e.target.checked)}
              className="w-4 h-4"
            />
            <span>Show Additional</span>
          </label>
        </div>
      </div>

      {/* Content Section */}
      <div className="px-8 pb-8">

        {/* College grid - 4 columns */}
        <div className="grid grid-cols-4 gap-2">
          {displayColleges.map(college => {
            const isSelected = selectedColleges.includes(college.id);
            const isDisabled = !isSelected && selectedColleges.length >= 4;
            const colors = getTierColors(college.category);
            const fullName = college.global_colleges?.name || college.name;
            const collegeName = fullName?.replace(/University/g, 'U').toUpperCase() || 'UNKNOWN';
            const tier = college.category?.toLowerCase() || 'other';

            // Get location from global_colleges
            const city = college.global_colleges?.city || '';
            const state = college.global_colleges?.state || '';
            const location = city && state ? `${city}, ${state}` : college.global_colleges?.location || '';

            return (
              <button
                key={college.id}
                onClick={() => handleToggleCollege(college.id)}
                disabled={isDisabled}
                className={`
                  bg-white rounded-lg overflow-hidden transition-all cursor-pointer
                  ${isSelected
                    ? `border-2 ${colors.selectedBorder}`
                    : `border-2 ${colors.unselectedBorder} border-dashed opacity-40 hover:opacity-60`
                  }
                `}
              >
                {/* College header with name and location */}
                <div className={`${colors.headerBg} px-4 py-3 flex items-center justify-between`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-xs truncate">{collegeName}</h3>
                    {location && (
                      <p className="text-white/80 text-xs truncate">{location}</p>
                    )}
                  </div>
                  {/* Checkmark for selected cards */}
                  {isSelected && (
                    <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                      <svg className={`w-3 h-3 ${colors.checkmarkColor}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Tier badge */}
                <div className="p-2 text-center">
                  <span className={`text-xs font-bold uppercase ${isSelected ? 'text-slate-600' : 'text-stone-400'}`}>
                    {tier === 'additional' ? 'ADD' : tier}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Empty state */}
        {displayColleges.length === 0 && (
          <div className="text-center py-6 text-stone-500 text-sm">
            {includeAdditional
              ? 'No colleges available for comparison'
              : 'No colleges in main tiers. Toggle "Additional" to see more options.'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeSelector;